import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// Headless: holds the single notifications WebSocket for the whole app (mounted
// once in Layout). On each pushed unread count it updates the badge query
// directly and refreshes the list; it reconnects with exponential backoff and
// silently degrades to the existing polling fallback if the socket can't be
// established (e.g. a local dev proxy without WS support).
const RECONNECT_BASE_MS = 5000;
const RECONNECT_MAX_MS = 60000;

const NotificationSocket = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    // No WebSocket (e.g. jsdom under test) → rely on the polling fallback.
    if (typeof WebSocket === 'undefined') return undefined;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let closedByUs = false;
    let attempts = 0;

    const connect = () => {
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const url = `${proto}://${window.location.host}/ws/notifications/`;

      try {
        socket = new WebSocket(url);
      } catch {
        scheduleReconnect();
        return;
      }

      socket.onopen = () => {
        attempts = 0;
      };

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);

          if (typeof payload.unread === 'number') {
            queryClient.setQueryData(
              ['/api/notifications/unread-count', undefined],
              { unread: payload.unread },
            );
          }
          queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
        } catch {
          /* ignore malformed frames */
        }
      };

      socket.onclose = () => {
        if (!closedByUs) scheduleReconnect();
      };

      socket.onerror = () => {
        socket?.close();
      };
    };

    const scheduleReconnect = () => {
      if (closedByUs) return;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempts, RECONNECT_MAX_MS);

      attempts += 1;
      reconnectTimer = setTimeout(connect, delay);
    };

    connect();

    return () => {
      closedByUs = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [queryClient]);

  return null;
};

export default NotificationSocket;
